import PropTypes from "prop-types";
import React from "react";
import morningAgenda from "../../data/program/morningAgenda";
import afternoonSessions from "../../data/program/afternoonSessions";

const img = (name) => `${process.env.PUBLIC_URL}/images/icsi26photo/${name}`;

const morningHighlights = [
  {
    title: "Tutorial",
    speaker: "Morning tutorial session",
    image: "tutorial.jpg",
    summary:
      "The day started with a tutorial session that set the academic tone for the conference and introduced the audience to current research directions.",
  },
  {
    title: "Opening Ceremony",
    speaker: "Opening remarks",
    image: "opening-ceremony-01.jpg",
    summary:
      "The opening ceremony formally launched ICSI'2026 in Beijing and welcomed all onsite and online participants to the main conference day.",
  },
  {
    title: "Plenary Speech I",
    speaker: morningAgenda[2].speaker,
    image: "plenary speech1.jpg",
    summary:
      "The first plenary talk drew strong attention from the audience and opened the morning with broad perspectives on swarm intelligence research.",
  },
  {
    title: "Group Photo + Tea Break",
    speaker: "Networking session",
    image: "group photo.jpg",
    summary:
      "Participants gathered for a group photo and continued discussions during the tea break, creating a lively and collegial atmosphere.",
  },
  {
    title: "Plenary Speech II",
    speaker: morningAgenda[4].speaker,
    image: "plenary speech2.jpg",
    summary:
      "The second plenary speech continued the morning program with a focused presentation and active audience engagement.",
  },
  {
    title: "Invited Speech I",
    speaker: morningAgenda[5].speaker,
    image: "invited speech1.jpg",
    summary:
      "The first invited talk shared recent advances and applications, with attendees following closely and taking notes throughout the session.",
  },
  {
    title: "Invited Speech II",
    speaker: morningAgenda[6].speaker,
    image: "invited speech2.jpg",
    summary:
      "The second invited talk concluded the technical morning program and added further insights from an application-oriented perspective.",
  },
  {
    title: "Awards Ceremony",
    speaker: "Best Paper and Best Student Paper awards",
    image: "award ceremony.jpg",
    summary:
      "The award ceremony recognized outstanding contributions presented at ICSI'2026 and celebrated the achievements of the award-winning authors.",
  },
];

const onsiteRoom = afternoonSessions.find((room) =>
  room.name.includes("Onsite Room")
);
const presenterOverrides = {
  4949: {
    displayPresenter: "Jiaqi Xie",
    note: "Photo shows Jiaqi Xie presenting onsite; the program listing names Ben Niu.",
    summary:
      "This onsite presentation was delivered by Jiaqi Xie and focused on machine learning models for predicting outpatient waiting time in maternal and child healthcare.",
  },
};

const afternoonTalks = (onsiteRoom?.items || [])
  .filter((item) => item.type === "talk" && item.presenter !== "Anupam Sharma")
  .map((item, index) => ({
    ...item,
    displayPresenter:
      presenterOverrides[item.paperId]?.displayPresenter || item.presenter,
    presenterNote: presenterOverrides[item.paperId]?.note || "",
    summary:
      presenterOverrides[item.paperId]?.summary ||
      `This session featured the paper "${item.title}" in the area of ${item.domain.toLowerCase()}.`,
    image: `author-talk-${String(index + 1).padStart(2, "0")}.jpg`,
  }));

const WorkDetailsContainer = () => {
  return (
    <div className="section section-padding gallery-2026-page">
      <div className="container">
        <div className="row pt--100 pb--60">
          <div className="col-lg-4 col-md-6 col-12">
            <div className="work-left work-details" data-aos="fade-up">
              <div className="portfolio-main-info">
                <h2 className="title">ICSI&apos;2026 Gallery</h2>
                <div className="work-details-list mt-12">
                  <div className="details-list">
                    <label>Year</label>
                    <span>2026</span>
                  </div>
                  <div className="details-list">
                    <label>Date</label>
                    <span>July 17–20</span>
                  </div>
                  <div className="details-list">
                    <label>Location</label>
                    <span>Vision Hotel, Beijing, China</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-6 offset-lg-1 col-12">
            <div className="work-left work-details mt-6">
              <div className="work-main-info">
                <div className="work-content">
                  <h4 className="title" data-aos="fade-up">
                    A Chronological Record of ICSI&apos;2026
                  </h4>
                  <div className="desc mt-8">
                    <p>
                      ICSI&apos;2026 was successfully held in Beijing, bringing
                      together scholars, speakers, authors, and participants for
                      a memorable exchange on swarm intelligence research.
                    </p>
                    <p>
                      This page records some of the key moments of the
                      conference, from the morning tutorial and opening
                      ceremony to the afternoon onsite oral presentations and
                      award session.
                    </p>
                    <p>
                      The conference concluded in a warm and celebratory
                      atmosphere, reflecting the enthusiasm, friendship, and
                      academic vitality shared by the ICSI community.
                    </p>
                    <p>
                      We sincerely thank all contributors and participants for
                      making ICSI&apos;2026 a successful and joyful gathering, and
                      we look forward to meeting again at future ICSI events.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="gallery-section-block">
          <div className="row">
            <div className="col-lg-4 col-12">
              <h3 className="heading heading-h3">Morning Program</h3>
              <p className="gallery-section-intro">
                The morning sessions followed the official program from tutorial
                to awards ceremony, with participants fully engaged throughout
                the day.
              </p>
            </div>
            <div className="col-lg-8 col-12">
              <div className="gallery-timeline">
                {morningHighlights.map((item) => (
                  <article className="gallery-event-card" key={item.title}>
                    <div className="gallery-event-image">
                      <img
                        src={img(item.image)}
                        alt={`${item.title} at ICSI 2026`}
                      />
                    </div>
                    <div className="gallery-event-content">
                      <h4>{item.title}</h4>
                      <p className="gallery-event-speaker">{item.speaker}</p>
                      <p>{item.summary}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="gallery-section-block">
          <div className="row align-items-center">
            <div className="col-lg-6 col-12">
              <img
                className="gallery-wide-photo"
                src={img("audience.jpg")}
                alt="Audience attending ICSI 2026"
              />
            </div>
            <div className="col-lg-6 col-12 mt-8 mt-lg-0">
              <h3 className="heading heading-h3">Afternoon Onsite Oral Session</h3>
              <p className="gallery-section-intro">
                In the afternoon, the onsite room hosted oral presentations
                covering optimization methods, machine learning, embodied AI,
                and application-oriented research. The sequence below follows
                the actual speaking order of the onsite session. The names shown
                below mainly follow the conference program and session record;
                in a very small number of cases, the photographed speaker may
                differ from the listed name.
              </p>
            </div>
          </div>
        </section>

        <section className="gallery-section-block">
          <div className="gallery-talk-grid">
            {afternoonTalks.map((talk) => (
              <article className="gallery-talk-card" key={talk.paperId}>
                <div className="gallery-talk-photo">
                  <img
                    src={img(talk.image)}
                    alt={`${talk.displayPresenter} presenting at ICSI 2026`}
                  />
                </div>
                <div className="gallery-talk-body">
                  <div className="gallery-talk-meta">
                    <span>Paper {talk.paperId}</span>
                  </div>
                  <h4>{talk.displayPresenter}</h4>
                  {talk.presenterNote && (
                    <p className="gallery-talk-presenter-note">
                      {talk.presenterNote}
                    </p>
                  )}
                  <p className="gallery-talk-domain">{talk.domain}</p>
                  <p className="gallery-talk-title">{talk.title}</p>
                  <p className="gallery-talk-summary">{talk.summary}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

WorkDetailsContainer.propTypes = {
  data: PropTypes.object,
};

export default WorkDetailsContainer;
